import React, { Component, useContext, Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { noop, isEmpty, isEqual } from 'lodash-es';

// Models
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import {
  PartnerOfferModel,
  buildLicensePlateStateCodeForVinPath,
  getLicensePlateErrorState,
} from 'client/data/models/partner-offer-model';
import {
  VehicleVinModel,
  buildStylesBasicPathFromVin,
  getVinStylesDataWithMetadata,
  VehicleVinEntities,
} from 'client/data/models/vehicle-vin';
import { ApiMetrics, getMetricApiOptions, withMetrics } from 'client/utils/metrics-hoc';

// Utils
import { validation } from 'site-modules/shared/components/form-validation/validation';
import { EventToolbox } from 'client/utils/event-toolbox';
import { venomHistory } from 'client/utils/history/venom-history';
import { getCreateListingUrl } from 'site-modules/shared/utils/private-party/private-party';
import {
  getLicensePlateTabStyleProperties,
  redactLicensePlateString,
} from 'site-modules/shared/components/appraisal/appraisal-tabs/license-plate-tab/license-plate-tab-utils';
import { delayHeavyTask } from 'client/site-modules/shared/utils/inp-utils';
import {
  fireAppraisalHowManyVinsPixel,
  fireAppraisalPixel,
  fireLicensePlateServicePixel,
  fireDecodePixels,
  fireVinErrorTracking,
} from 'site-modules/shared/utils/appraisal/common-utils';

// Context
import { AppraisalTabsContext } from 'site-modules/shared/components/appraisal/appraisal-tabs/appraisal-tabs-context';

// Constants
import { TrackingConstant } from 'client/tracking/constant';
import { APPRAISAL_VEHICLE_ENTRY_CREATIVE_ID } from 'site-modules/shared/constants/appraisal/appraisal';
import { APPRAISAL_STORAGE_KEYS } from 'site-modules/shared/components/appraisal/trade-in-appraiser-steps/license-plate-and-vin-step/constants';
import { LICENSE_PLATE_TAB } from 'site-modules/shared/components/appraisal/appraisal-tabs-container/appraisal-tab-constants';
import { PRIVATE_PARTY_EVENTS } from 'site-modules/shared/constants/private-party/private-party-events';
import {
  appraisalTabViewPropType,
  getResolvedAppraisalTabViewProps,
} from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/shared/appraisal-tab-view';

// Components
import { AppraisalTabsSubmitButton } from 'site-modules/shared/components/appraisal/appraisal-tabs-container/shared/appraisal-tabs-submit-button';
import { PrivatePartyButton } from 'site-modules/shared/components/appraisal/private-party-button/private-party-button';
import { LicensePlateTab } from './license-plate-tab-content';
import { LicensePlateTabLpInput } from './license-plate-tab-lp-input';
import { LicensePlateTabStateCodeInput } from './license-plate-tab-state-code-input';

const ERROR_MESSAGE_MAP = {
  licensePlate: {
    INVALID: 'Please enter a valid license plate',
    LANDING_INVALID: vinTabLink => (
      <Fragment>
        We could not find that license plate. If your license plate is a special plate (vanity, disabled person, special
        interest, etc.), please use your {vinTabLink} instead.
      </Fragment>
    ),
    ERROR: "We're sorry, we could not look up your license plate at this time.",
    NO_STYLES: "We're sorry. We were unable to locate your license plate. Please try entering a VIN instead.",
  },
  stateCode: {
    INVALID: 'Please select a state',
  },
};

export const commonEventData = {
  action_name: TrackingConstant.ACTION_APPRAISE_VEHICLE,
  action_category: TrackingConstant.USER_ACTION_CATEGORY,
  action_cause: TrackingConstant.ACTION_CAUSE_BUTTON_CLICK,
};

const getIsReadyForUpdate = (props, state) => {
  const {
    vinFromLicensePlate,
    licensePlate,
    stateCode,
    vinEligibility,
    isAppraisalValuePage,
    isLicensePlateAndVinStep,
  } = props;
  const { wasFormSubmitted, isSubmitting, licensePlateInputValue, stateCodeInputValue, isMultipleVinProcessed } = state;
  const vinValueFromLp = vinFromLicensePlate?.vin;
  const vinLength = vinFromLicensePlate?.vins?.length;
  const isMultiVins = vinLength > 1;

  return (
    wasFormSubmitted &&
    (isMultiVins ? isMultipleVinProcessed : isSubmitting) && // this enables checks for vin set in multi-vins modal after the form has been submitted
    vinFromLicensePlate &&
    licensePlateInputValue === licensePlate &&
    stateCodeInputValue === stateCode &&
    // wait for vinEligibility if /appraisal-value/ page and not license plate and VIN step
    (isAppraisalValuePage && vinValueFromLp ? !isEmpty(vinEligibility) || isLicensePlateAndVinStep : true)
  );
};

export class LicensePlateTabContainerUI extends Component {
  state = {
    licensePlateInputValue: this.props.licensePlate,
    stateCodeInputValue: this.props.stateCode,
    isLpValid: true,
    errorMap: new Map(),
    isSubmitting: false,
    wasFormSubmitted: false,
    isMultipleVinProcessed: false,
  };

  componentDidMount() {
    const { storage } = this.props;
    window.addEventListener('pageshow', this.setDefaultButtonStateForCachedPage);

    if (storage) {
      const storedLp = storage.get(APPRAISAL_STORAGE_KEYS.LICENSE_PLATE);
      const storageStateCode = storage.get(APPRAISAL_STORAGE_KEYS.STATE_CODE);

      let storageLp;
      try {
        const parsed = JSON.parse(storedLp); // Handles the case when an empty string was stored so we don't use the serialized JSON empty string
        storageLp = parsed ? String(parsed) : parsed;
      } catch {
        storageLp = storedLp;
      }

      if (storageStateCode) {
        this.onStateCodeChange({ stateCode: storageStateCode }, false);
      }
      if (storageLp) {
        this.onLicensePlateChange({ target: { value: storageLp } }, false);
      }
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      vinFromLicensePlate,
      licensePlateLookupError,
      vinTabLink,
      vinEligibility,
      isAppraisalValuePage,
      onCompleteCallback,
      vin: vinFromContext,
      isAppraiserPageOpened,
      creativeId,
    } = this.props;
    const { isSellInCityState, isWIDG1125chal1, isPrivatePartyBtn } = getResolvedAppraisalTabViewProps(this.props);
    const { isSubmitting, errorMap, isMultipleVinProcessed } = this.state;
    const vinValueFromLp = vinFromLicensePlate?.vin;
    const vinLength = vinFromLicensePlate?.vins?.length;
    const isMultiVins = vinLength > 1;

    if (vinFromLicensePlate && !isEqual(vinFromLicensePlate, prevProps.vinFromLicensePlate)) {
      fireAppraisalHowManyVinsPixel('license_plate_lookup_vins_found', vinLength, creativeId);
    }

    if (typeof vinValueFromLp === 'string' && vinValueFromLp !== prevProps.vinFromLicensePlate?.vin) {
      if (vinValueFromLp) {
        fireAppraisalPixel('License Plate', creativeId);
        if (vinFromLicensePlate.service === 'experian')
          fireLicensePlateServicePixel('vin_found_experian', vinValueFromLp, creativeId);
        else {
          fireLicensePlateServicePixel('vin_found_ihs', vinValueFromLp, creativeId);
          fireLicensePlateServicePixel('vin_not_found_experian', null, creativeId);
        }
      } else {
        fireLicensePlateServicePixel('vin_not_found_ihs', null, creativeId);
        fireLicensePlateServicePixel('vin_not_found_experian', null, creativeId);
      }
    }

    // Don't block the CTA for the multi-VINs case
    if (isMultiVins && isSubmitting) {
      this.resetIsSubmitting();
    }

    // Reset submitting state when /appraisal-value/ page is opened
    if (isAppraiserPageOpened && !prevProps.isAppraiserPageOpened) {
      this.resetIsSubmitting();
    }

    if (isMultiVins && isMultipleVinProcessed && !vinFromContext && !licensePlateLookupError) {
      // User hasn't selected a VIN from the multiple VINs, but license plate lookup was successful
      this.fireLicensePlateLookupTracking(TrackingConstant.VIN_FOUND);
      this.setIsMultipleVinProcessed(false);
      return;
    }

    if (getIsReadyForUpdate(this.props, this.state) && !getIsReadyForUpdate(prevProps, prevState)) {
      // Happy path when VIN is found
      if (!isMultiVins && vinValueFromLp && !licensePlateLookupError) {
        this.fireLicensePlateLookupTracking(TrackingConstant.VIN_FOUND);

        // We don't need to set style IDs while on /appraisal-value/ page
        if (!isAppraisalValuePage) {
          this.setStylesIds(vinValueFromLp);
        }

        if (isWIDG1125chal1 && isSellInCityState) {
          venomHistory.push(`/sell-car/instant-cash-offer/?icoVin=${vinValueFromLp}`);
          return;
        }

        if (isPrivatePartyBtn) {
          EventToolbox.fireCustomEvent(PRIVATE_PARTY_EVENTS.START_PP_LISTING, {
            value: LICENSE_PLATE_TAB.label,
            creativeId,
          });
          venomHistory.push(getCreateListingUrl(vinValueFromLp));
          return;
        }

        onCompleteCallback(vinValueFromLp, vinEligibility);
        this.resetIsSubmitting();
        return;
      }

      if (isMultiVins && vinFromContext && !licensePlateLookupError) {
        // User has selected a VIN from multi-vin modal
        onCompleteCallback(vinFromContext, vinEligibility);
        this.setIsMultipleVinProcessed(false);
        return;
      }

      const updatedErrorMap = new Map(errorMap);
      // Handles null/empty VIN.
      if (vinValueFromLp === null || licensePlateLookupError) {
        updatedErrorMap.set('licensePlate', ERROR_MESSAGE_MAP.licensePlate.ERROR);
        updatedErrorMap.delete('licensePlateWide');
      } else {
        updatedErrorMap.set('licensePlateWide', ERROR_MESSAGE_MAP.licensePlate.LANDING_INVALID(vinTabLink));
        updatedErrorMap.delete('licensePlate');
      }

      this.fireLicensePlateLookupTracking(TrackingConstant.VIN_NOT_FOUND);

      this.setState({ isSubmitting: false, errorMap: updatedErrorMap, isLpValid: false });
    }
  }

  componentWillUnmount() {
    window.removeEventListener('pageshow', this.setDefaultButtonStateForCachedPage);
  }

  onLicensePlateChange = (e, triggerOnValueChange = true) => {
    const { storage, licensePlate } = this.props;

    const value = e.target.value;
    this.setState({ licensePlateInputValue: value }, () => {
      this.clearFormState('licensePlate');

      if (triggerOnValueChange && licensePlate !== value) {
        this.props.onValueChange();
      }

      if (storage) {
        storage.set(APPRAISAL_STORAGE_KEYS.LICENSE_PLATE, value);
      }
    });
  };

  onStateCodeChange = (option, triggerOnValueChange = true) => {
    const { storage, stateCode } = this.props;

    const value = option.stateCode;
    this.setState({ stateCodeInputValue: value }, () => {
      this.clearFormState('stateCode');

      if (triggerOnValueChange && stateCode !== value) {
        this.props.onValueChange();
      }

      if (storage) {
        storage.set(APPRAISAL_STORAGE_KEYS.STATE_CODE, value);
      }
    });
  };

  onSubmit = e => {
    const { setVin, clearVin, setMmy, setStyleIds, clearDecodedVinEligibility } = this.props;

    e.preventDefault();

    delayHeavyTask(async () => {
      setMmy({});
      setStyleIds(null);
      await clearVin(); // clear VIN from parent
      setVin('');
      clearDecodedVinEligibility(); // clear decodedVinEligibility to prepare the next step
      this.validateAndSubmit();
    });
  };

  setDefaultButtonStateForCachedPage = ({ persisted }) => {
    if (persisted) {
      this.resetIsSubmitting();
      this.setState({ wasFormSubmitted: false });
    }
  };

  setVinDecodeError = () => {
    const { vinTabLink, isAppraisalValuePage } = this.props;
    const { errorMap } = this.state;

    const updatedErrorMap = new Map(errorMap);

    updatedErrorMap.set(
      'licensePlateWide',
      !isAppraisalValuePage
        ? ERROR_MESSAGE_MAP.licensePlate.LANDING_INVALID(vinTabLink)
        : ERROR_MESSAGE_MAP.licensePlate.NO_STYLES
    );
    updatedErrorMap.delete('licensePlate');

    this.setState({
      errorMap: updatedErrorMap,
      isSubmitting: false,
      isLpValid: false,
    });
  };

  setStylesIds = async vinValue => {
    const {
      setStylesAndVin,
      licensePlate,
      setStyleIds: setContextStyleIds,
      apiMetrics,
      creativeId,
      isLpAndVinFormComponent,
      setColors,
    } = this.props;
    const vinStylesData = await getVinStylesDataWithMetadata({
      vin: vinValue,
      apiOptions: getMetricApiOptions(apiMetrics),
    });
    const { results: styles = [], exteriorColor, interiorColor } = vinStylesData;

    // <LicensePlateAndVinForm /> component handles this pixel separately
    if (!isLpAndVinFormComponent) {
      fireDecodePixels({
        stylesLength: styles?.length,
        vinStylesData,
        creativeId,
      });
      if (vinStylesData?.isPubStateInvalid) {
        fireVinErrorTracking(vinValue, vinStylesData, creativeId);
      }
    }

    setColors({ exteriorColor, interiorColor });
    setStylesAndVin(styles, vinValue, licensePlate);
    const styleIds = styles.map(({ styleId }) => styleId);

    setContextStyleIds(styleIds);
  };

  setIsMultipleVinProcessed = value => {
    this.setState({ isMultipleVinProcessed: value });
  };

  resetIsSubmitting = () => {
    this.setState({ isSubmitting: false });
  };

  clearFormState = errorMapKey => {
    const { errorMap } = this.state;

    const updatedErrorMap = new Map(errorMap);

    updatedErrorMap.delete(errorMapKey);

    this.setState({
      isSubmitting: false,
      wasFormSubmitted: false,
      errorMap: updatedErrorMap,
    });
  };

  validateAndSubmit = () => {
    const { creativeId, setLicensePlate, setStateCode } = this.props;
    const { licensePlateInputValue, stateCodeInputValue, errorMap } = this.state;

    const isValidLicensePlate = validation.validateLicensePlate(licensePlateInputValue);
    const isValidStateCode = validation.validateValue(stateCodeInputValue);
    const isLpInputValid = isValidStateCode && isValidLicensePlate;

    const updatedErrorMap = new Map(errorMap);

    if (isValidLicensePlate) {
      updatedErrorMap.delete('licensePlate');
      updatedErrorMap.delete('licensePlateWide');
    } else {
      updatedErrorMap.set('licensePlate', ERROR_MESSAGE_MAP.licensePlate.INVALID);
      updatedErrorMap.delete('licensePlateWide');
    }
    if (isValidStateCode) {
      updatedErrorMap.delete('stateCode');
    } else {
      updatedErrorMap.set('stateCode', ERROR_MESSAGE_MAP.stateCode.INVALID);
    }

    this.setState(
      {
        errorMap: updatedErrorMap,
        isLpValid: isLpInputValid,
        isSubmitting: isLpInputValid,
        wasFormSubmitted: true,
      },
      () => {
        if (!isLpInputValid) {
          const { onValidationError } = this.props;
          onValidationError();
        }
        // Set LP and State code to get VIN data
        if (isLpInputValid) {
          setLicensePlate(licensePlateInputValue);
          setStateCode(stateCodeInputValue);

          const fireTracking = (eventType, subactionName, value) => {
            EventToolbox.fireTrackAction({
              event_type: eventType,
              event_data: {
                ...commonEventData,
                subaction_name: subactionName,
                creative_id: creativeId,
                value,
              },
            });
          };

          fireTracking(
            TrackingConstant.EVENT_TYPE_ACTION_PROGRESS,
            TrackingConstant.SUBMIT_VEHICLE,
            TrackingConstant.LICENSE_PLATE_SPACE
          );
          fireTracking(
            TrackingConstant.EVENT_TYPE_ACTION_START,
            TrackingConstant.LICENSE_PLATE,
            redactLicensePlateString(licensePlateInputValue)
          );
          fireTracking(
            TrackingConstant.EVENT_TYPE_ACTION_START,
            TrackingConstant.LICENSE_PLATE_STATE,
            stateCodeInputValue
          );
        }
      }
    );
  };

  fireLicensePlateLookupTracking = value => {
    const { creativeId } = this.props;
    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_PROGRESS,
      event_data: {
        ...commonEventData,
        subaction_name: TrackingConstant.LICENSE_PLATE_LOOKUP,
        creative_id: creativeId,
        value,
      },
    });
  };

  render() {
    const {
      vinFromLicensePlate,
      creativeId,
      ctaText,
      hideVinLicensePlateBtn,
      tabContentModal,
      decodedVinError,
      vinDecodeResponse,
      vehicle,
      isAppraisalValuePage,
      onMultipleVinsModalOpen,
      onMultipleVinsModalClose,
      isProjectEveStyle,
      isLicensePlateAndVinStep,
      isVinByAddressOverlay,
      isEscapeHatch,
      isActiveTab,
      view,
    } = this.props;
    const { isEmoSearchEnabled, isLookUpStyleView, isPrivatePartyBtn, isAddressTab } = getResolvedAppraisalTabViewProps(
      this.props
    );
    const {
      licensePlateInputValue,
      stateCodeInputValue,
      isLpValid,
      errorMap,
      isSubmitting,
      wasFormSubmitted,
      isMultipleVinProcessed,
    } = this.state;

    const componentStyleProperties = getLicensePlateTabStyleProperties({
      ...this.props,
      ...getResolvedAppraisalTabViewProps(this.props),
    });
    const hasError = !isEmpty(errorMap);

    return (
      <LicensePlateTab
        vehicle={vehicle}
        componentStyleProperties={componentStyleProperties}
        isSubmitting={isSubmitting}
        wasFormSubmitted={wasFormSubmitted}
        isLpValid={isLpValid}
        errorMap={errorMap}
        creativeId={creativeId}
        ctaText={ctaText}
        setVinDecodeError={this.setVinDecodeError}
        vinFromLicensePlate={vinFromLicensePlate}
        hideVinLicensePlateBtn={hideVinLicensePlateBtn}
        tabContentModal={tabContentModal}
        decodedVinError={decodedVinError}
        vinDecodeResponse={vinDecodeResponse}
        isEmoSearchEnabled={isEmoSearchEnabled}
        isAppraisalValuePage={isAppraisalValuePage}
        isLookUpStyleView={isLookUpStyleView}
        {...(view ? { view } : {})}
        setIsMultipleVinProcessed={this.setIsMultipleVinProcessed}
        onMultipleVinsModalOpen={onMultipleVinsModalOpen}
        onMultipleVinsModalClose={onMultipleVinsModalClose}
        isMultipleVinProcessed={isMultipleVinProcessed}
        isLicensePlateAndVinStep={isLicensePlateAndVinStep}
        setStylesIds={this.setStylesIds}
        isActiveTab={isActiveTab}
        lpInputSlot={
          <LicensePlateTabLpInput
            licensePlateInputValue={licensePlateInputValue}
            onLicensePlateChange={this.onLicensePlateChange}
            errorMap={errorMap}
            {...componentStyleProperties}
          />
        }
        stateCodeInputSlot={
          <LicensePlateTabStateCodeInput
            stateCodeInputValue={stateCodeInputValue}
            onStateCodeChange={this.onStateCodeChange}
            errorMap={errorMap}
            {...componentStyleProperties}
          />
        }
        submitButtonSlot={
          isPrivatePartyBtn ? (
            <PrivatePartyButton
              onClick={this.onSubmit}
              isSubmitting={isSubmitting}
              isDisabled={isSubmitting || (wasFormSubmitted && !isLpValid)}
              data-test="submit-license-plate-btn"
              isActiveTab={isActiveTab}
              buttonClassName="mt-lg-1_75"
            />
          ) : (
            <AppraisalTabsSubmitButton
              containerClassName={classnames({
                'mt-0_5': componentStyleProperties.isLookUpStyleView,
                'mt-lg-1_75': componentStyleProperties.isBackgroundStyleTabEnabled || isProjectEveStyle,
              })}
              buttonClassName="btn-responsive-md-down"
              onSubmit={this.onSubmit}
              ctaText={ctaText}
              isSubmitting={isSubmitting}
              isDisabled={isSubmitting || (wasFormSubmitted && !isLpValid)}
              data-test="submit-license-plate-btn"
              isActiveTab={isActiveTab}
            />
          )
        }
        overlaySubmitButtonSlot={
          <AppraisalTabsSubmitButton
            block
            buttonClassName={classnames('heading-5 text-white epo-license-plate-continue-btn btn-responsive-md-down', {
              'py-1': isEscapeHatch && !isVinByAddressOverlay,
              'py-0_5': !isEscapeHatch && !isVinByAddressOverlay,
            })}
            onSubmit={this.onSubmit}
            ctaText={ctaText}
            isSubmitting={isSubmitting || (wasFormSubmitted && isVinByAddressOverlay && !hasError)}
            isDisabled={isSubmitting || (wasFormSubmitted && (!isLpValid || (isVinByAddressOverlay && !hasError)))}
            data-tracking-id="epo_submit_license_plate"
            data-test="submit-license-plate-btn"
            data-tracking-value={componentStyleProperties.isSecondary ? `${ctaText} 2` : ctaText}
            aria-label={ctaText}
            isActiveTab={isActiveTab || isAddressTab}
          />
        }
      />
    );
  }
}

LicensePlateTabContainerUI.propTypes = {
  licensePlate: PropTypes.string,
  stateCode: PropTypes.string,
  setVin: PropTypes.func.isRequired,
  setLicensePlate: PropTypes.func.isRequired,
  setStateCode: PropTypes.func.isRequired,
  setStyleIds: PropTypes.func.isRequired,
  setColors: PropTypes.func.isRequired,
  setMmy: PropTypes.func.isRequired,
  clearVin: PropTypes.func,
  onCompleteCallback: PropTypes.func,
  onValueChange: PropTypes.func,
  vehicle: PropTypes.shape({
    make: PropTypes.shape({
      slug: PropTypes.string,
    }),
    model: PropTypes.shape({
      slug: PropTypes.string,
    }),
    year: PropTypes.shape({
      year: PropTypes.string,
    }),
  }),
  vinEligibility: PropTypes.shape({}),
  vinFromLicensePlate: PropTypes.shape({
    vin: PropTypes.string,
    service: PropTypes.string,
    vins: PropTypes.arrayOf(PropTypes.string),
  }),
  vin: PropTypes.string,
  creativeId: PropTypes.string,
  ctaText: PropTypes.string,
  licensePlateLookupError: PropTypes.bool,
  vinTabLink: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  setStylesAndVin: PropTypes.func,
  clearDecodedVinEligibility: PropTypes.func,
  vinDecodeResponse: PropTypes.shape({
    results: VehicleVinEntities.VinStylesBasicInfo,
    status: PropTypes.number,
    hasError: PropTypes.bool,
  }),
  hideVinLicensePlateBtn: PropTypes.bool,
  tabContentModal: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  decodedVinError: PropTypes.oneOfType([PropTypes.string, PropTypes.node, PropTypes.func]),
  isVinByAddressOverlay: PropTypes.bool,
  isBackgroundStyleTabEnabled: PropTypes.bool,
  isVdpEmbedded: PropTypes.bool,
  isAppraisalValuePage: PropTypes.bool,
  isEscapeHatchDrawer: PropTypes.bool,
  isLocationStyleStep: PropTypes.bool,
  isEscapeHatch: PropTypes.bool,
  isLookUpStyleView: PropTypes.bool,
  isFullWidth: PropTypes.bool,
  isSecondary: PropTypes.bool,
  isLicensePlateAndVinStep: PropTypes.bool,
  isMobile: PropTypes.bool,
  isEmoSearchEnabled: PropTypes.bool,
  onMultipleVinsModalOpen: PropTypes.func,
  onMultipleVinsModalClose: PropTypes.func,
  isAppraiserPageOpened: PropTypes.bool,
  isSellInCityState: PropTypes.bool,
  isWIDG1125chal1: PropTypes.bool,
  storage: PropTypes.shape({}),
  isProjectEveStyle: PropTypes.bool,
  isAddressTab: PropTypes.bool,
  apiMetrics: ApiMetrics,
  isLpAndVinFormComponent: PropTypes.bool,
  isActiveTab: PropTypes.bool,
  isPrivatePartyBtn: PropTypes.bool,
  onValidationError: PropTypes.func,
  view: appraisalTabViewPropType,
};

LicensePlateTabContainerUI.defaultProps = {
  licensePlate: '',
  stateCode: '',
  vehicle: null,
  vinFromLicensePlate: null,
  vinEligibility: null,
  vin: '',
  creativeId: APPRAISAL_VEHICLE_ENTRY_CREATIVE_ID,
  setStylesAndVin: noop,
  clearVin: noop,
  onCompleteCallback: noop,
  onValueChange: noop,
  clearDecodedVinEligibility: noop,
  ctaText: '',
  licensePlateLookupError: false,
  vinTabLink: null,
  vinDecodeResponse: null,
  hideVinLicensePlateBtn: false,
  tabContentModal: null,
  decodedVinError: '',
  isVinByAddressOverlay: false,
  isBackgroundStyleTabEnabled: false,
  isVdpEmbedded: false,
  isAppraisalValuePage: false,
  isEscapeHatchDrawer: false,
  isLocationStyleStep: false,
  isEscapeHatch: false,
  isLookUpStyleView: false,
  isFullWidth: false,
  isSecondary: false,
  isLicensePlateAndVinStep: false,
  isMobile: false,
  isEmoSearchEnabled: false,
  onMultipleVinsModalOpen: noop,
  onMultipleVinsModalClose: noop,
  isAppraiserPageOpened: false,
  isSellInCityState: false,
  isWIDG1125chal1: false,
  storage: null,
  isProjectEveStyle: false,
  isAddressTab: false,
  apiMetrics: undefined,
  isLpAndVinFormComponent: false,
  isActiveTab: false,
  isPrivatePartyBtn: false,
  onValidationError: noop,
  view: null,
};

export const stateToPropsConfig = {
  vinDecodeResponse: bindToPath(({ vin }) => buildStylesBasicPathFromVin({ vin }), VehicleVinModel),
  vinFromLicensePlate: bindToPath(
    ({ licensePlate, stateCode }) => buildLicensePlateStateCodeForVinPath({ licensePlate, stateCode }),
    PartnerOfferModel
  ),
};

export const mapStateToProps = state => ({
  licensePlateLookupError: getLicensePlateErrorState(state),
  isAppExtensionPage: state.pageContext?.isAppExtensionPage,
});

const LicensePlateTabWrapper = connect(mapStateToProps)(
  connectToModel(withMetrics(LicensePlateTabContainerUI), stateToPropsConfig)
);

export function LicensePlateTabContainer(props) {
  const {
    vin,
    licensePlate,
    stateCode,
    setVin,
    setLicensePlate,
    setStateCode,
    setStyleIds,
    setMmy,
    onMultipleVinsModalOpen,
    onMultipleVinsModalClose,
    isAppraiserPageOpened,
    isProjectEveStyle,
    setColors,
  } = useContext(AppraisalTabsContext);

  return (
    <LicensePlateTabWrapper
      {...props}
      vin={vin}
      licensePlate={licensePlate}
      stateCode={stateCode}
      setVin={setVin}
      setLicensePlate={setLicensePlate}
      setStateCode={setStateCode}
      setStyleIds={setStyleIds}
      setMmy={setMmy}
      onMultipleVinsModalOpen={onMultipleVinsModalOpen}
      onMultipleVinsModalClose={onMultipleVinsModalClose}
      isAppraiserPageOpened={isAppraiserPageOpened}
      isProjectEveStyle={isProjectEveStyle}
      setColors={setColors}
    />
  );
}
